import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Col,
  Grid,
  Descriptions,
  Image,
  List,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  LoadingOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import authCheck from "../../../APIs/authCheck";
import PageTitle from "../../../Components/PageTitle/pageTitle";
import userServices from "../../../APIs/userServices";

const { useBreakpoint } = Grid;

const Profile = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [messageApi, contextHolder] = message.useMessage();

  const [isLoading, setIsLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [userDetails, setUserDetails] = useState();

  //#region Quotes
  const colorMap = {
    Pending: "orange",
    Contacted: "blue",
    Approved: "green",
    Rejected: "red",
    Closed: "red",
  };

  const [userQuotes, setUserQuotes] = useState([]);
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [openQuoteDetails, setOpenQuoteDetails] = useState(false);

  let pageSizeVar = 3;

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: pageSizeVar,
    total: 0,
  });

  const columns = [
    {
      title: "Reference",
      dataIndex: "referenceId",
      key: "referenceId",
      sorter: (a, b) => a.referenceId.localeCompare(b.referenceId),
      render: (text, record) => {
        return screens.xs ? (
          <span
            style={{
              color: "#002753",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onClick={() => {
              setQuoteDetails(record);
              setOpenQuoteDetails(true);
            }}
          >
            <RightOutlined
              className="moving-arrow"
              style={{
                marginRight: 6,
                fontSize: 10,
              }}
            />

            <i>{text}</i>
          </span>
        ) : (
          <Tooltip title="View Quote Details">
            <span
              style={{
                color: "#002753",
                fontWeight: "bold",
                cursor: "pointer",
              }}
              onClick={() => {
                setQuoteDetails(record);
                setOpenQuoteDetails(true);
              }}
            >
              <RightOutlined
                className="moving-arrow"
                style={{
                  marginRight: 6,
                  fontSize: 10,
                }}
              />

              <i>{text}</i>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      responsive: ["md"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      filters: [
        { text: "Pending", value: "Pending" },
        { text: "Approved", value: "Approved" },
      ],
      render: (status) => (
        <Tag
          color={colorMap[status] || "default"}
          style={{
            textTransform: "capitalize",
            borderRadius: 10,
            border: "none",
          }}
        >
          {status}
        </Tag>
      ),
    },
    // {
    //   title: "",
    //   key: "actions",
    //   render: (e) => (
    //     <Tooltip title="View Quote Details">
    //       <Button
    //         key="viewDetails"
    //         type="primary"
    //         onClick={() => {
    //           setQuoteDetails(e);
    //           setOpenQuoteDetails(true);
    //         }}
    //         style={{
    //           padding: 10,
    //         }}
    //       >
    //         <EyeOutlined
    //           style={{
    //             color: "#002753",
    //           }}
    //         />
    //       </Button>
    //     </Tooltip>
    //   ),
    // },
  ];
  //#endregion

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authCheck.authCheck();

        // console.log("Auth check response:", response);

        if (response?.status === 200) setIsAuthenticated(true);
        else navigate("/login");
      } catch (error) {
        console.error("Authentication check failed:", error.response?.data);

        messageApi.error(
          error.response?.data || "Failed to authenticate. Please try again."
        );

        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) getProfileDetails();
  }, [isAuthenticated]);

  const getProfileDetails = async (page = 1, pageSize = pageSizeVar) => {
    try {
      if (localStorage.getItem("uniqueKey") === null) {
        navigate("/login");

        return;
      }

      setIsPaginationLoading(true);

      const skip = (page - 1) * pageSize;
      const take = pageSize;

      const response = await userServices.getProfileDetails({ skip, take });

      if (response?.status === 200) {
        // messageApi.success("Profile details fetched successfully!");

        // console.log("Profile details:", response.data);

        const userData = [
          {
            title: "Full Name",
            description: response.data.firstName + " " + response.data.lastName,
            icon: (
              <UserOutlined
                style={{
                  color: "#6B7280",
                }}
              />
            ),
          },
          {
            title: "Email",
            description: response.data.emailAddress,
            icon: (
              <MailOutlined
                style={{
                  color: "#6B7280",
                }}
              />
            ),
          },
          {
            title: "Phone Number",
            description: response.data.phoneNumber,
            icon: (
              <PhoneOutlined
                style={{
                  color: "#6B7280",
                }}
              />
            ),
          },
          {
            title: "Date of Birth",
            description: dayjs(response.data.dateOfBirth).format(
              "DD MMMM YYYY"
            ),
            icon: (
              <CalendarOutlined
                style={{
                  color: "#6B7280",
                }}
              />
            ),
          },
          {
            title: "Shipping Address",
            description: response.data.userAddress,
            icon: <EnvironmentOutlined style={{ color: "#6B7280" }} />,
          },
        ];

        setUserDetails(userData);

        // Assuming userQuotes is part of the response
        if (response.data.userQuotes) {
          //map response.data.userQuotes into a var
          const mappedQuotes = response.data.userQuotes.map((quote) => ({
            referenceId: quote.referenceNumber,
            date: dayjs(quote.createdDate).format("DD MMM YYYY - h.mm A"),
            status: quote.status,
            specialInstructions: quote.specialInstructions,
            quotationData: JSON.parse(quote.quotationData),
          }));

          // console.log("Mapped user quotes:", mappedQuotes);

          setUserQuotes(mappedQuotes);

          setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize,
            total: response.data.totalQuotes,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching profile details:", error.response.data);

      messageApi.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to fetch profile details. Please try again."
      );
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
    }
  };

  return (
    <Space direction="vertical" className="page-container">
      {isLoading && (
        <Spin
          spinning={isLoading}
          indicator={<LoadingOutlined spin />}
          fullscreen
        />
      )}

      {contextHolder}

      <PageTitle
        title="Your Account"
        subTitle="Manage your profile, view your cart, and track your quotes easily."
        buttonText="Logout"
      />

      <Row
        gutter={[16, 16]}
        justify="space-between"
        style={{
          marginTop: screens.xs ? 20 : 0,
        }}
      >
        <Col lg={12} sm={24} xs={24}>
          <Card
            title="Profile Details"
            styles={{
              header: {
                borderBottom: "none",
                color: "#002753",
                fontWeight: "bold",
                fontSize: 24,
              },
              body: {
                paddingTop: 0,
              },
            }}
          >
            <List
              loading={{
                spinning: isLoading,
                indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
              }}
              style={{
                textAlign: "left",
              }}
              locale={{ emptyText: "No user details available." }}
              itemLayout="horizontal"
              dataSource={userDetails}
              renderItem={(item, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          height: 32,
                          marginRight: 0,
                          marginTop: 10,
                          marginBottom: 0,
                        }}
                        icon={<span style={{ fontSize: 18 }}>{item.icon}</span>}
                      />
                    }
                    title={
                      <p
                        style={{
                          color: "#6B7280",
                          fontSize: 14,
                          fontWeight: "normal",
                        }}
                      >
                        {item.title}
                      </p>
                    }
                    description={
                      <p
                        style={{
                          color: "#002753",
                          fontSize: 16,
                          fontWeight: "normal",
                        }}
                      >
                        {item.description}
                      </p>
                    }
                  />
                </List.Item>
              )}
            />

            {/* <Button
              type="primary"
              style={{
                marginTop: 16,
                width: "100%",
                backgroundColor: "#002753",
                padding: 20,
              }}
              onClick={() => {
                console.log("Edit Profile Clicked");
              }}
            >
              <EditOutlined /> Edit Profile
            </Button> */}
          </Card>
        </Col>

        <Col lg={12} sm={24} xs={24}>
          <Card
            title="Previous Quotes"
            styles={{
              header: {
                borderBottom: "none",
                color: "#002753",
                fontWeight: "bold",
                fontSize: 24,
              },
              body: {
                paddingTop: 0,
              },
            }}
            style={{
              marginBottom: 20,
            }}
          >
            <Table
              loading={{
                spinning: isLoading || isPaginationLoading,
                indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
              }}
              locale={{ emptyText: "No previous quotes available." }}
              rowKey="referenceId"
              columns={columns}
              dataSource={userQuotes}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: [5, 10, 20],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total}`,
              }}
              onChange={(pag) => getProfileDetails(pag.current, pag.pageSize)}
            />
          </Card>

          <Card
            title="Need Help?"
            styles={{
              header: {
                borderBottom: "none",
                color: "#002753",
                fontWeight: "bold",
                fontSize: 24,
                textAlign: "center",
              },
              body: {
                paddingTop: 0,
              },
            }}
            style={{
              textAlign: "center",
            }}
          >
            <p
              style={{
                color: "#4B5563",
                fontSize: 16,
              }}
            >
              Have questions or need assistance?
            </p>

            <Button
              type="primary"
              style={{
                marginTop: 16,
                backgroundColor: "#002753",
                padding: 20,
              }}
              onClick={() => {
                navigate("/contact-us");
              }}
            >
              <MessageOutlined /> Contact Us
            </Button>
          </Card>
        </Col>
      </Row>

      <Modal
        centered
        width={800}
        open={openQuoteDetails}
        title={`Reference - (${quoteDetails?.referenceId})`}
        onCancel={() => setOpenQuoteDetails(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            style={{
              backgroundColor: "#002753",
              marginTop: 10,
            }}
            onClick={() => setOpenQuoteDetails(false)}
          >
            Close
          </Button>,
        ]}
        destroyOnClose={true}
      >
        {/* <p>
              <strong>Reference ID:</strong> {quoteDetails?.referenceId}
            </p> */}

        <p>
          <strong>Date:</strong>{" "}
          {dayjs(quoteDetails?.date).format("DD MMMM YYYY")}
        </p>

        <p>
          <strong>Status:</strong>{" "}
          <Tag
            color={quoteDetails?.status === "Pending" ? "orange" : "green"}
            style={{
              textTransform: "capitalize",
              borderRadius: 10,
              border: "none",
            }}
          >
            {quoteDetails?.status}
          </Tag>
        </p>

        <p
          style={{
            marginBottom: 10,
          }}
        >
          <strong>Special Instructions:</strong> <br />
          {quoteDetails?.specialInstructions}
        </p>

        <List
          dataSource={quoteDetails?.quotationData}
          bordered
          style={{
            maxHeight: 400,
            overflowY: "auto",
            position: "relative",
          }}
          renderItem={(item) => (
            <List.Item style={{ alignItems: "flex-start", padding: 12 }}>
              <div style={{ display: "flex", width: "100%", gap: 16 }}>
                {/* Product image */}
                <Image
                  width={80}
                  height={80}
                  src="https://react-slick.neostack.com/img/react-slick/abstract01.jpg"
                  // src={`/uploads/products/${item.FirstImage}`}
                  alt={item.ProductName}
                  style={{ objectFit: "cover", borderRadius: 4 }}
                  fallback="/no-image.png" // optional fallback if image missing
                />

                {/* Product details */}
                <Descriptions
                  size="small"
                  column={4}
                  style={{ flex: 1 }}
                  labelStyle={{ fontWeight: 500 }}
                  layout={screens.xs ? "vertical" : "horizontal"}
                >
                  <Descriptions.Item label="Product Name" span={4}>
                    {item.ProductName}
                  </Descriptions.Item>

                  <Descriptions.Item label="SKU" span={screens.xs ? 2 : 1}>
                    {item.Sku}
                  </Descriptions.Item>

                  <Descriptions.Item label="Quantity" span={screens.xs ? 2 : 3}>
                    {item.Quantity}
                  </Descriptions.Item>

                  <Descriptions.Item label="Description" span={4}>
                    {item.Descriptions}
                  </Descriptions.Item>

                  {Object.entries(item.Options).map(([key, value]) => (
                    <Descriptions.Item
                      key={key}
                      label={key}
                      span={screens.xs ? 1 : 4}
                    >
                      {value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </div>
            </List.Item>
          )}
        />
      </Modal>
    </Space>
  );
};

export default Profile;
